import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

const NotificationManager = () => {
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');
    const [viewMode, setViewMode] = useState('main'); // 'main', 'configure', or 'logs'
    const [isLoading, setIsLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const logsPerPage = 10;

    // Separate state for each report type's recipients
    const [lookerRecipients, setLookerRecipients] = useState([]);
    const [festiveRecipients, setFestiveRecipients] = useState([]);
    const [domainsRecipients, setDomainsRecipients] = useState([]);

    // Separate state for each form's input values
    const [lookerInputs, setLookerInputs] = useState({ userName: '', userGmail: '' });
    const [festiveInputs, setFestiveInputs] = useState({ userName: '', userGmail: '' });
    const [domainsInputs, setDomainsInputs] = useState({ userName: '', userGmail: '' });

    const setTimedMessage = (msg, type) => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => {
            setMessage(null);
            setMessageType('');
        }, 5000);
    };

    // This useEffect hook runs when viewMode changes
    useEffect(() => {
        const fetchRecipients = async () => {
            setIsLoading(true);
            try {
                const [lookerRes, festiveRes, domainsRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/looker-studio`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/festival-alert`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/domain-expiring`),
                ]);
                setLookerRecipients(lookerRes.data.recipients || []);
                setFestiveRecipients(festiveRes.data.recipients || []);
                setDomainsRecipients(domainsRes.data.recipients || []);
            } catch (error) {
                console.error('Failed to fetch recipients:', error);
                setTimedMessage('Failed to load recipient details.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        const fetchLogs = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/logs`);
                setLogs(response.data);
                setCurrentPage(1); // Reset to first page when logs are loaded
            } catch (error) {
                console.error('Failed to fetch logs:', error);
                setTimedMessage('Failed to load logs.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        if (viewMode === 'configure') {
            fetchRecipients();
        } else if (viewMode === 'logs') {
            fetchLogs();
        }
    }, [viewMode]);

    const handleSendReport = async (reportType) => {
        setIsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${reportType}`);
            setTimedMessage(response.data.message, 'success');
        } catch (error) {
            console.error(`Failed to send ${reportType} report:`, error);
            setTimedMessage(error.response?.data?.message || 'Sending failed, please try again.', 'error');
        } finally {
            setIsLoading(false);
            setViewMode('main');
        }
    };

    // Function to handle changes in specific input fields
    const handleInputChange = (reportType, field, value) => {
        if (reportType === 'looker-studio') {
            setLookerInputs(prev => ({ ...prev, [field]: value }));
        } else if (reportType === 'festival-alert') {
            setFestiveInputs(prev => ({ ...prev, [field]: value }));
        } else if (reportType === 'domain-expiring') {
            setDomainsInputs(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleAddRecipient = async (reportType) => {
        setIsLoading(true);
        let inputs;
        if (reportType === 'looker-studio') {
            inputs = lookerInputs;
        } else if (reportType === 'festival-alert') {
            inputs = festiveInputs;
        } else if (reportType === 'domain-expiring') {
            inputs = domainsInputs;
        }

        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/${reportType}`, {
                userName: inputs.userName,
                userGmail: inputs.userGmail
            });
            setTimedMessage('Recipient added successfully!', 'success');
            
            // Refresh recipients after adding
            const [lookerRes, festiveRes, domainsRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/looker-studio`),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/festival-alert`),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/domain-expiring`),
            ]);
            setLookerRecipients(lookerRes.data.recipients || []);
            setFestiveRecipients(festiveRes.data.recipients || []);
            setDomainsRecipients(domainsRes.data.recipients || []);

            // Reset the specific form fields
            if (reportType === 'looker-studio') {
                setLookerInputs({ userName: '', userGmail: '' });
            } else if (reportType === 'festival-alert') {
                setFestiveInputs({ userName: '', userGmail: '' });
            } else if (reportType === 'domain-expiring') {
                setDomainsInputs({ userName: '', userGmail: '' });
            }

        } catch (error) {
            console.error('Failed to add recipient:', error);
            setTimedMessage(error.response?.data?.message || 'Failed to add recipient.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteRecipient = async (reportType, userGmail) => {
        setIsLoading(true);
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/${reportType}/${userGmail}`);
            setTimedMessage('Recipient deleted successfully!', 'success');
            // Refresh recipients after deleting
            const [lookerRes, festiveRes, domainsRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/looker-studio`),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/festival-alert`),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/recipients/domain-expiring`),
            ]);
            setLookerRecipients(lookerRes.data.recipients || []);
            setFestiveRecipients(festiveRes.data.recipients || []);
            setDomainsRecipients(domainsRes.data.recipients || []);
        } catch (error) {
            console.error('Failed to delete recipient:', error);
            setTimedMessage('Failed to delete recipient.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const renderRecipientList = (recipients, reportType) => (
        <ul className="space-y-2">
            {recipients.map((recipient, index) => (
                <li key={index} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div>
                        <div className="font-semibold text-gray-800">{recipient.userName}</div>
                        <div className="text-sm text-gray-500">{recipient.userGmail}</div>
                    </div>
                    <button
                        onClick={() => handleDeleteRecipient(reportType, recipient.userGmail)}
                        className="bg-red-500 hover:bg-red-600 text-white font-bold py-1 px-3 rounded-full transition duration-200 ease-in-out transform hover:scale-105"
                        disabled={isLoading}
                    >
                        &times;
                    </button>
                </li>
            ))}
        </ul>
    );

    return (
        <div className="bg-gray-100 min-h-screen p-8 flex items-center justify-center font-sans">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
                <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Notification Manager</h1>

                {message && (
                    <div className={`p-4 mb-4 rounded-lg text-white ${messageType === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
                        {message}
                    </div>
                )}

                {viewMode === 'main' && (
                    <div className="space-y-4">
                        <button
                            onClick={() => handleSendReport('looker-studio')}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md disabled:bg-indigo-400"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Looker Studio Report'}
                        </button>
                        <button
                            onClick={() => handleSendReport('festival-alert')}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md disabled:bg-indigo-400"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Festive Alert'}
                        </button>
                        <button
                            onClick={() => handleSendReport('domain-expiring')}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md disabled:bg-indigo-400"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Domains Expiring'}
                        </button>
                        <hr className="my-4" />
                        <button
                            onClick={() => setViewMode('configure')}
                            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                        >
                            Configure Recipients
                        </button>
                        <button
                            onClick={() => setViewMode('logs')}
                            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                        >
                            View Logs
                        </button>
                    </div>
                )}

                {viewMode === 'configure' && (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-semibold text-center text-gray-700">Configure Recipients</h2>
                        
                        {/* Looker Studio Report */}
                        <div className="p-6 bg-gray-50 rounded-lg shadow-sm">
                            <h3 className="text-xl font-bold mb-4 text-gray-700">Looker Studio Report</h3>
                            <form onSubmit={(e) => { e.preventDefault(); handleAddRecipient('looker-studio'); }} className="space-y-4 mb-4">
                                <div>
                                    <label htmlFor="lookerName" className="block text-gray-700 font-medium mb-1">User Name</label>
                                    <input
                                        type="text"
                                        id="lookerName"
                                        value={lookerInputs.userName}
                                        onChange={(e) => handleInputChange('looker-studio', 'userName', e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                        placeholder="Enter user name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lookerEmail" className="block text-gray-700 font-medium mb-1">Gmail</label>
                                    <input
                                        type="email"
                                        id="lookerEmail"
                                        value={lookerInputs.userGmail}
                                        onChange={(e) => handleInputChange('looker-studio', 'userGmail', e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                        placeholder="Enter email address"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md disabled:bg-indigo-400"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Adding...' : 'Add Recipient'}
                                </button>
                            </form>
                            {lookerRecipients.length > 0 ? (
                                renderRecipientList(lookerRecipients, 'looker-studio')
                            ) : (
                                <p className="text-center text-gray-500">No recipients configured.</p>
                            )}
                        </div>

                        {/* Festive Alert */}
                        <div className="p-6 bg-gray-50 rounded-lg shadow-sm">
                            <h3 className="text-xl font-bold mb-4 text-gray-700">Festive Alert</h3>
                            <form onSubmit={(e) => { e.preventDefault(); handleAddRecipient('festival-alert'); }} className="space-y-4 mb-4">
                                <div>
                                    <label htmlFor="festiveName" className="block text-gray-700 font-medium mb-1">User Name</label>
                                    <input
                                        type="text"
                                        id="festiveName"
                                        value={festiveInputs.userName}
                                        onChange={(e) => handleInputChange('festival-alert', 'userName', e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                        placeholder="Enter user name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="festiveEmail" className="block text-gray-700 font-medium mb-1">Gmail</label>
                                    <input
                                        type="email"
                                        id="festiveEmail"
                                        value={festiveInputs.userGmail}
                                        onChange={(e) => handleInputChange('festival-alert', 'userGmail', e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                        placeholder="Enter email address"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md disabled:bg-indigo-400"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Adding...' : 'Add Recipient'}
                                </button>
                            </form>
                            {festiveRecipients.length > 0 ? (
                                renderRecipientList(festiveRecipients, 'festival-alert')
                            ) : (
                                <p className="text-center text-gray-500">No recipients configured.</p>
                            )}
                        </div>

                        {/* Domains Expiring */}
                        <div className="p-6 bg-gray-50 rounded-lg shadow-sm">
                            <h3 className="text-xl font-bold mb-4 text-gray-700">Domains Expiring</h3>
                            <form onSubmit={(e) => { e.preventDefault(); handleAddRecipient('domain-expiring'); }} className="space-y-4 mb-4">
                                <div>
                                    <label htmlFor="domainsName" className="block text-gray-700 font-medium mb-1">User Name</label>
                                    <input
                                        type="text"
                                        id="domainsName"
                                        value={domainsInputs.userName}
                                        onChange={(e) => handleInputChange('domain-expiring', 'userName', e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                        placeholder="Enter user name"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="domainsEmail" className="block text-gray-700 font-medium mb-1">Gmail</label>
                                    <input
                                        type="email"
                                        id="domainsEmail"
                                        value={domainsInputs.userGmail}
                                        onChange={(e) => handleInputChange('domain-expiring', 'userGmail', e.target.value)}
                                        className="w-full border-2 border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                                        placeholder="Enter email address"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md disabled:bg-indigo-400"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Adding...' : 'Add Recipient'}
                                </button>
                            </form>
                            {domainsRecipients.length > 0 ? (
                                renderRecipientList(domainsRecipients, 'domain-expiring')
                            ) : (
                                <p className="text-center text-gray-500">No recipients configured.</p>
                            )}
                        </div>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={() => setViewMode('main')}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                )}

                {viewMode === 'logs' && (
                    <div className="space-y-4">
                        <h2 className="text-2xl font-semibold text-center text-gray-700">Combined Report Logs</h2>
                        {isLoading ? (
                            <p className="text-center text-gray-500">Loading logs...</p>
                        ) : logs.length > 0 ? (
                            <>
                                <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Recipient Name
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Recipient Email
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Report Type
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Sent At
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {logs
                                                .slice((currentPage - 1) * logsPerPage, currentPage * logsPerPage)
                                                .map((log) => (
                                                    <tr key={log._id}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.recipientName}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.recipientEmail}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{log.reportType?.replace(/-/g, ' ')}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {format(new Date(log.sentAt), 'yyyy-MM-dd HH:mm:ss')}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* Pagination Controls */}
                                <div className="flex justify-center items-center space-x-2 mt-4">
                                    <button
                                        className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold disabled:opacity-50"
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                    <span className="px-2 text-gray-700 font-medium">
                                        Page {currentPage} of {Math.ceil(logs.length / logsPerPage)}
                                    </span>
                                    <button
                                        className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold disabled:opacity-50"
                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(logs.length / logsPerPage)))}
                                        disabled={currentPage === Math.ceil(logs.length / logsPerPage)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p className="text-center text-gray-500">No report logs found.</p>
                        )}
                        <div className="flex justify-center">
                            <button
                                type="button"
                                onClick={() => setViewMode('main')}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                            >
                                Back to Main
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationManager;
